import { Box } from '@ltvco/refresh-lib/theme';
import {
  KeyboardEvent,
  forwardRef,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import {
  AnimatedInputButtonContainer,
  AnimatedInputContainer,
  AnimatedSendButton,
  ArrowUpwardIcon,
  ClickHereTooltip,
  SendButton,
  StyledInputChat,
} from 'components/ChatBV/components';

import { DeepSearchContext } from '../context';

export interface DeepSearchChatInputRef {
  setValue: (value: string) => void;
}

interface DeepSearchChatInputProps {
  isLanding?: boolean;
}

export const DeepSearchChatInput = forwardRef<
  DeepSearchChatInputRef,
  DeepSearchChatInputProps
>(({ isLanding }, ref) => {
  const { sendMessage, isLoading } = useContext(DeepSearchContext);
  const [inputValue, setInputValue] = useState('');
  const inputElementRef = useRef<HTMLTextAreaElement>(null);

  const hasInputValue = inputValue.trim().length > 0;

  useImperativeHandle(ref, () => ({
    setValue: (value: string) => {
      setInputValue(value);
      inputElementRef.current?.focus();
    },
  }));

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }
    sendMessage(inputValue.trim());
    setInputValue('');
    if (inputElementRef.current) {
      inputElementRef.current.style.height = 'auto';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && inputValue.trim()) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <AnimatedInputContainer data-role="chat-input" isLanding={isLanding}>
      <AnimatedInputButtonContainer isLanding>
        <StyledInputChat
          ref={inputElementRef}
          placeholder="What can I help you find?"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          rows={1}
          sx={{ width: '100%' }}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            mt: 1,
          }}
        >
          <AnimatedSendButton>
            <SendButton onClick={handleSend} hasInputValue={hasInputValue}>
              <ArrowUpwardIcon hasInputValue={hasInputValue} />
            </SendButton>
          </AnimatedSendButton>
        </Box>
      </AnimatedInputButtonContainer>
      {!isLanding && (
        <Box
          component="span"
          display="block"
          fontSize="13px"
          color="text.secondary"
          textAlign="center"
          mt={2}
        >
          This chat feature is powered by artificial intelligence.{' '}
          <ClickHereTooltip>Click here</ClickHereTooltip> to view our AI usage
          disclaimer
        </Box>
      )}
    </AnimatedInputContainer>
  );
});
